import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { CheckCircle2Icon, InfoIcon, TriangleAlertIcon, type LucideIcon } from "lucide-react"

import { cn } from "../../lib/utils"

const alertVariants = cva(
  "grid gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4 w-full relative group/alert",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        // Light mode uses emerald-tinted surface with dark text so the
        // alert reads on a white background. Dark mode keeps the
        // original translucent emerald-on-dark treatment.
        success:
          "border-emerald-500/30 bg-emerald-50 text-emerald-900 *:data-[slot=alert-description]:text-emerald-800/90 *:[svg]:text-emerald-600 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-50 dark:*:data-[slot=alert-description]:text-emerald-100/85 dark:*:[svg]:text-emerald-300",
        destructive:
          "text-destructive bg-card *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

type AlertVariant = VariantProps<typeof alertVariants>["variant"]

const AlertVariantContext = React.createContext<{ variant: AlertVariant }>({
  variant: "default",
})

const titleIconByVariant: Record<NonNullable<AlertVariant>, LucideIcon> = {
  default: InfoIcon,
  success: CheckCircle2Icon,
  destructive: TriangleAlertIcon,
}

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  const resolvedVariant = variant ?? "default"

  return (
    <AlertVariantContext.Provider value={{ variant: resolvedVariant }}>
      <div
        data-slot="alert"
        role="alert"
        className={cn(alertVariants({ variant: resolvedVariant }), className)}
        {...props}
      />
    </AlertVariantContext.Provider>
  )
}

function AlertTitle({
  className,
  icon,
  hideIcon = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  icon?: LucideIcon
  hideIcon?: boolean
}) {
  const { variant } = React.useContext(AlertVariantContext)
  const Icon = icon ?? titleIconByVariant[variant ?? "default"]

  return (
    <div
      data-slot="alert-title"
      className={cn(
        "flex items-center gap-2 [&_a]:hover:text-foreground font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3",
        className
      )}
      {...props}
    >
      {!hideIcon && Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
      <span>{children}</span>
    </div>
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground [&_a]:hover:text-foreground text-sm text-balance md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_p:not(:last-child)]:mb-4",
        className
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 right-2", className)}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction }
